// Answers "where exactly is the document on screen right now?". "Content" is
// the DOCUMENT being marked up — the version's image (<img>) or pdf page
// (<canvas>) — the background the markup is drawn over, not the markup
// itself. It letterboxes inside the stage, and all markup geometry is stored
// as percentages OF THE CONTENT — so the overlay's position and every
// pointer→percent conversion depend on this rect. Null until the content has
// real dimensions; re-measured automatically on any resize (ResizeObserver
// on both elements) and on `contentKey` changes.

import { useLayoutEffect, useState, type RefObject } from 'react';
import type { IScreenRect } from '@utils/svgMarkers';

/** The rendered content element under the stage — what the overlay anchors to. */
export function contentOf(stage: HTMLElement | null): HTMLElement | null {
  return stage?.querySelector<HTMLElement>('img, canvas:not([data-overlay])') ?? null;
}

export function useContentRect(
  stageRef: RefObject<HTMLDivElement | null>,
  /** Re-measures when this changes — a PDF page flip resizes the same canvas. */
  contentKey: string | number,
): IScreenRect | null {
  const [contentRect, setContentRect] = useState<IScreenRect | null>(null);

  useLayoutEffect(() => {
    const stage = stageRef.current;
    const content = contentOf(stage);
    if (!stage || !content) return;
    const measure = () => {
      const stageRect = stage.getBoundingClientRect();
      const rect = content.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return; // image not loaded yet
      const next: IScreenRect = {
        left: rect.left - stageRect.left,
        top: rect.top - stageRect.top,
        width: rect.width,
        height: rect.height,
      };
      setContentRect((prev) =>
        prev && prev.left === next.left && prev.top === next.top &&
        prev.width === next.width && prev.height === next.height
          ? prev
          : next,
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(stage);
    observer.observe(content);
    return () => observer.disconnect();
    // stageRef is a stable ref object; contentKey is the real re-measure cue.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentKey]);

  return contentRect;
}